import { useCallback, useMemo, useState } from "react";

import { addWeeks, getDate, getMonth, getYear } from "date-fns";
import { Frequency, RRule, Weekday } from "rrule";

import { parseRecurrenceRule } from "~/stream/utils";
import type { SelectorOption } from "~/ui/selectors/SelectorUtils";

import { useRecurrenceRuleModal } from "../EventForm/CustomRecurrenceRuleModal";

import type {
	CustomRecurringRuleSerializerInput,
	frequency as FrequencyChoice,
	repeat as RepeatChoice,
	Choices as WeekDayChoices,
} from "~/__generated__/useSaveEventMutationMutation.graphql";

export const weekDays: WeekDayChoices[] = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const ordinalLabels = ["first", "second", "third", "fourth", "fifth"] as const;
const frequencyMap: Record<Frequency, FrequencyChoice> = {
	[Frequency.YEARLY]: "YEARLY",
	[Frequency.MONTHLY]: "MONTHLY",
	[Frequency.WEEKLY]: "WEEKLY",
	[Frequency.DAILY]: "DAILY",
	[Frequency.HOURLY]: "DAILY",
	[Frequency.MINUTELY]: "DAILY",
	[Frequency.SECONDLY]: "DAILY",
};

/**
 * Converts a date into the format of "\<ordinal> \<day of week>", e.g. "first Monday".
 * @param date The date to convert.
 * @returns The formatted string.
 */
export function getWeekOfMonthText(date: Date) {
	const dayName = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date);
	const weekOfMonth = Math.floor((getDate(date) - 1) / 7) + 1;
	const sameDayNextWeek = addWeeks(date, 1);
	const isLast = [getYear(sameDayNextWeek), getMonth(sameDayNextWeek)] > [getYear(date), getMonth(date)];

	if (isLast) {
		return `last ${dayName}`;
	} else {
		return `${ordinalLabels[weekOfMonth - 1]} ${dayName}`;
	}
}

export interface RepeatOption extends SelectorOption {
	repeat: RepeatChoice;
}

const initialState = {
	repeatState: "NEVER" as RepeatChoice,
	customRrule: {
		interval: 1,
		frequency: "WEEKLY" as FrequencyChoice,
		weekDays: [],
	},
};

export function getRepeatStateFromRrule(
	rruleStrings: ReadonlyArray<string | null> | null,
): [RepeatChoice, CustomRecurringRuleSerializerInput] {
	if (!rruleStrings || rruleStrings.length === 0) return ["NEVER", initialState.customRrule];

	const rrule = parseRecurrenceRule(rruleStrings);
	const options = rrule?.options;
	const freq = rrule?.options.freq;
	const interval = rrule?.options.interval;
	const byweekday = rrule?.options.byweekday;
	const everyWeekDay = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR];
	const isEveryWeekDay =
		byweekday && byweekday.length === 5 && everyWeekDay.every(day => byweekday.includes(day.n || 0));

	let matchedRepeatState: RepeatChoice | null = null;

	if (!options?.count && !options?.until) {
		// Could try to convert to existing repeat options
		if (freq === Frequency.DAILY && interval === 1) {
			matchedRepeatState = "DAILY";
		} else if (freq === Frequency.WEEKLY) {
			if (interval === 1) {
				if (isEveryWeekDay) {
					matchedRepeatState = "EVERYWEEKDAY";
				} else if (byweekday && byweekday.length === 1) {
					matchedRepeatState = "WEEKLY";
				}
			} else if (interval === 2 && byweekday && byweekday.length === 1) {
				matchedRepeatState = "BIWEEKLY";
			}
		} else if (freq === Frequency.MONTHLY && interval === 1) {
			matchedRepeatState = "MONTHLY";
		}
	}

	if (matchedRepeatState) {
		return [matchedRepeatState, initialState.customRrule];
	}

	return [
		"CUSTOM",
		{
			interval: interval || 1,
			frequency: freq ? frequencyMap[freq] : initialState.customRrule.frequency,
			weekDays: byweekday ? (byweekday.map(nDay => new Weekday(nDay).toString()) as WeekDayChoices[]) : [],
			untilDate: options && options.until ? options.until.toISOString() : null,
			untilCount: options?.count || null,
			monthlyRepeat: options?.byweekday ? "RELATIVE" : "ABSOLUTE",
		},
	];
}

export default function useRecurrenceOptions({
	startDate,
	initialOption,
}: {
	startDate?: Date;
	initialOption?: RepeatChoice;
}) {
	const openCustomRecurringRuleModal = useRecurrenceRuleModal();
	const [repeatState, setRepeatState] = useState<RepeatChoice>(initialOption || initialState.repeatState);
	const [customRrule, setCustomRrule] = useState<CustomRecurringRuleSerializerInput>(initialState.customRrule);

	const repeatOptions: Array<RepeatOption> = useMemo(() => {
		const weekday = startDate && new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(startDate);
		const dateInYear =
			startDate && new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" }).format(startDate);

		return [
			{ value: "NEVER", repeat: "NEVER", label: "Does not repeat" },
			{ value: "DAILY", repeat: "DAILY", label: "Daily" },
			{
				value: "WEEKLY",
				repeat: "WEEKLY",
				label: `Weekly${weekday ? ` on ${weekday}` : ""}`,
			},
			{
				value: "BIWEEKLY",
				repeat: "BIWEEKLY",
				label: `Bi-Weekly${weekday ? ` on ${weekday}` : ""}`,
			},
			{ value: "EVERYWEEKDAY", repeat: "EVERYWEEKDAY", label: "Every weekday (Mon-Fri)" },
			{
				value: "MONTHLY",
				repeat: "MONTHLY",
				label: `Monthly${startDate ? ` on the ${getWeekOfMonthText(startDate)}` : ""}`,
			},
			{ value: "YEARLY", repeat: "YEARLY", label: `Annually${dateInYear ? ` on ${dateInYear}` : ""}` },
			{ value: "CUSTOM", repeat: "CUSTOM", label: "Custom…" },
		];
	}, [startDate]);

	const changeRecurrence = useCallback(
		async (value: RepeatChoice) => {
			if (value !== "CUSTOM") return setRepeatState(value);

			const result = await openCustomRecurringRuleModal({
				value: customRrule,
				setValue: setCustomRrule,
				eventStart: startDate,
			});

			if (result) return setRepeatState("CUSTOM");
		},
		[openCustomRecurringRuleModal, startDate, customRrule],
	);

	const resetRecurrenceValues = useCallback(() => {
		setRepeatState(initialState.repeatState);
		setCustomRrule(initialState.customRrule);
	}, []);

	const setRecurrenceFromRruleCallback = useCallback((rruleStrings: ReadonlyArray<string | null> | null) => {
		const [repeat, custom] = getRepeatStateFromRrule(rruleStrings);
		setRepeatState(repeat);
		setCustomRrule(custom);
	}, []);

	return {
		repeatOptions,
		repeatState,
		changeRecurrence,
		customRrule: repeatState === "CUSTOM" ? customRrule : null,
		resetRecurrenceValues,
		setRecurrenceFromRrule: setRecurrenceFromRruleCallback,
	};
}
